// services/AuthService.ts
import type { ApiResponse } from "@/types/common";
import { privateRequest, publicRequest } from "../axios";
import { type AxiosResponse } from "axios";

// Define the request/response types
export interface LoginRequest {
  email: string;
  reference:string;
  otp: string;
}

export interface OTPRequest {
  email: string;
}

export interface BackendPermission {
  id: number;
  actionName: string;
  description: string;
}

export interface BackendLoginResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    userId: number;
    userName: string;
    token: string;
    role: string;
    permissions: BackendPermission[];
  };
}

interface ForgotPasswordRequest {
  email: string;
}

interface VerifyOtpRequest {
  email: string;
  code: string;
}

interface ResetPasswordRequest {
  email: string;
  otp: string;
  newPassword: string;
}

const ENDPOINTS = {
  LOGIN: "/auth/login",
  SEND_OTP: "/auth/send-otp",
  LOGOUT: "/auth/logout",
  FORGOT_PASSWORD: "/auth/change-password",
  OTP_VERIFICATION: "/auth/verify",
  RESET_PASSWORD: "/auth/reset-password",
};

const login = async (data: LoginRequest) => {
  const response = await publicRequest.post<ApiResponse<string>>(
    ENDPOINTS.LOGIN,
    data
  );
  return response?.data;
};

const otpRequest = async (data: OTPRequest) => {
  const response = await publicRequest.post<ApiResponse<string>>(
    ENDPOINTS.SEND_OTP,
    data
  );
  return response?.data;
};

const logout = async (): Promise<{ message: string }> => {
  const response: AxiosResponse<{ message: string }> =
    await privateRequest.post(ENDPOINTS.LOGOUT);
  return response.data;
};

const forgotPassword = async (
  data: ForgotPasswordRequest
): Promise<{ message: string }> => {
  try {
    const response: AxiosResponse<{ message: string }> =
      await privateRequest.post(ENDPOINTS.FORGOT_PASSWORD, data);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

const verifyOtp = async (data: VerifyOtpRequest) => {
  try {
    const response = await publicRequest.post<ApiResponse<string>>(
      ENDPOINTS.OTP_VERIFICATION,
      data
    );
    return response?.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

const reset = async (
  data: ResetPasswordRequest
): Promise<{ message: string }> => {
  try {
    const response: AxiosResponse<{ message: string }> =
      await publicRequest.post(ENDPOINTS.RESET_PASSWORD, data);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

const AuthService = {
  login,
  otpRequest,
  logout,
  forgotPassword,
  verifyOtp,
  reset,
};

export default AuthService;
